import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { pick, isPlainObject} from 'lodash'

import { useEventListeners, useEventManager } from '@deathbyjer/react-event-manager'

import { gatherSelectedComponents, removeComponents, setSelected, useComponentCreator, useComponentDuplicate } from './componentSlice'

import DrawArea, { DUPLICATE_KEYS } from 'components/utility/draw_area'
import usePreparedComponents from './usePreparedComponents'
import { Tools } from '../../common/toolbar/Tools'
import useZoomHandler from 'components/form_builder/common/hooks/useZoomHandler'

const DELETE_KEY = [ 8, 46 ]
const CTRL_KEY = 17
const PLUS_KEY = 187
const MINUTE_KEY = 189
const DUPLICATE_KEY = 68

const EMPTY_FUNCTION = () => {}

function useGenerateCallback(page) {
  const createComponent = useComponentCreator()
  const activeToolName = useSelector(({tools}) => tools.active)
  const tool = Tools[activeToolName]

  return useCallback((point) => {
    if (!tool?.createsComponent)
      return

    const component = {
      page,
      type: tool.name,
      ...pick(tool.defaultAttributes, ['options', 'rect']),
      ...( isPlainObject(tool.createsComponent) ? tool.createsComponent : {} )
    }
    component.rect = { ...point, ...component.rect }

    createComponent(component)
  }, [ createComponent, page, tool ])
}

function usePageComponents(page) {
  const components = usePreparedComponents()

  return useMemo(() => {
    return components?.filter(component => component.props.page == page)
  }, [components, page])
}

function useEventMonitors({page}) {
  const dispatch = useDispatch()
  const selectedComponents = useSelector(state => state.components.selected )
  const { zoomIn, zoomOut, valueTransfrom } = useZoomHandler()
  const components = useSelector(gatherSelectedComponents, shallowEqual)
  const createDuplicateComponent = useComponentDuplicate()
  const rectRef = useRef()
  const events = useEventManager()

  const onMouseLeave = useCallback(() => (rectRef.current = null), [])

  const onMouseMove = useCallback((event) => {
    const pageEl = document.getElementById(`page-${page}`)
    if (!pageEl) return
    const pageElTop = pageEl.getBoundingClientRect().top
    const pageElLeft = pageEl.getBoundingClientRect().left
    const curTop = ((event.clientY - pageElTop) * 100) / (pageEl.clientHeight * valueTransfrom.current)
    const curLeft = ((event.clientX - pageElLeft) * 100) / (pageEl.clientWidth * valueTransfrom.current)
    rectRef.current = { x: curLeft, y: curTop }
  }, [])

  const handleDuplicate = useCallback(() => {
    if(!rectRef.current || !components.length) return 
    const newComponents = []
    for (const component of components) {
      if (component.type === 'data') {
        const rect = { ...component.rect, x: rectRef.current.x, y: rectRef.current.y }
        newComponents.push(createDuplicateComponent({ ...component, page: page }, rect))
      }
    }
    if (newComponents.length > 0) {
      const keys = newComponents.map((component) => [component.type, component.id, component.subtype])
      events.applyEventListeners('duplicateComponent', keys)
    }
  }, [components])

  const removeSelected = useCallback(() => {
    dispatch(removeComponents(selectedComponents))
  }, [dispatch, selectedComponents])

  const onClick = useCallback(evt => {}, [])

  const onKeyDown = useCallback(evt => {
    switch(evt.keyCode) {
      case DUPLICATE_KEY:
        if (evt.ctrlKey) handleDuplicate()
        break;
      case PLUS_KEY:
        if (!evt.ctrlKey) return
        evt.preventDefault() // prevent zoom of browser
        zoomIn()
        break;
      case MINUTE_KEY:
        if (!evt.ctrlKey) return
        evt.preventDefault() // prevent zoom of browser
        zoomOut()
        break
      default:
        break;
    }
  }, [ zoomIn, zoomOut, handleDuplicate ])

  const onKeyUp = useCallback(evt => {
    if (DELETE_KEY.includes(evt.keyCode)) removeSelected()
  }, [removeSelected])


  return useMemo(() => ({
    onClick, onKeyDown, onKeyUp, onMouseLeave, onMouseMove
  }), [onClick, onKeyDown, onKeyUp, onMouseLeave, onMouseMove])
}

const DocumentArea = ({page, id}) => {
  const dispatch = useDispatch()
  const currentPageWidth = useSelector(({pages}) => pages.pageWidth)
  const {width: pageWidth, height: pageHeight } = useSelector(({pages}) => pages.all[page].page_sizes[currentPageWidth])
  const {url: imageUrl } = useSelector(({pages}) => pages.all[page])
  const activeToolName = useSelector(({ tools }) => tools.active)
  const tool = Tools[activeToolName]
  const drawArea = useRef()

  useEventListeners({
    selectComponent: (componentId) => {
      drawArea.current?.selectChildren(componentId)
    },
    selectMultipleComponentsByDragging: (componentIds) => {
      drawArea.current?.selectMultipleChildrenByDragging(componentIds)
    },
    duplicateComponent: (componentIds) => {
        // this function only runs after new duplicate elements have been created on the DOM 
        setTimeout(() => {
          drawArea.current?.stopDragging()
          drawArea.current?.unselectAll()
          componentIds.map(componentId => drawArea.current?.selectChildren(componentId))
        }, 0);
    },
    resizeSelf: () => {
      drawArea.current?.resizeSelf()
    }
  })

  const { onKeyUp, onClick, onKeyDown, onMouseMove, onMouseLeave } = useEventMonitors({ page })

  const components = usePageComponents(page)
  const generate = useGenerateCallback(page)

  const updateSelected = useCallback(list => {
    dispatch(setSelected(list))
  }, [dispatch])

  const style = {
    aspectRatio: pageWidth / pageHeight,
    backgroundImage: `url(${imageUrl})`,
    cursor: tool?.defaultAttributes?.options?.cursor ?? 'default'
  }

  return (
    <div id={id} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} className="document-area" style={style}>
      <DrawArea
        ref={drawArea}
        onEmptyMousedown={generate}
        onUpdatedSelected={updateSelected}
        onKeyUp={onKeyUp}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        { components }
      </DrawArea>
    </div>

  )
}

export default DocumentArea
